/** @jsx jsx */
// The line above enables css`` to work for our Sparkline charts
import { gql, useQuery } from '@apollo/client';
import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Heading,
  Link,
  Spinner,
  Text,
  customColors,
} from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import { useCallback, useMemo } from 'react';

import { UNIDENTIFIED_CLIENT } from 'src/app/graph/clientsPage/constants';
import { clientsRouteConfig, insightsRouteConfig } from 'src/app/graph/routes';
import { DateRangeBadge } from 'src/components/dateRangeBadge/DateRangeBadge';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  OperationClientUsageSparklinesQuery,
  OperationClientUsageSparklinesQueryVariables,
} from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

import { useIsAppInBackgroundContext } from '../../app/appLayout/hooks/useIsAppInBackground';
import { date } from '../../lib/date/date';
import { SparklineChart } from '../charts/SparklineChart';
import { ClickableText } from '../common/clickableText/ClickableText';
import { ErrorMessage } from '../errorMessage/ErrorMessage';

// We only want to display the Top 4 Clients (by usage) when on the OLP
const LIMIT: number = 4;

export const TotalCountForSparklineFragment = gql`
  fragment TotalCountForSparklineFragment on ServiceQueryStatsRecord {
    metrics {
      totalLatencyHistogram {
        totalCount
      }
    }
    groupBy {
      clientName
      operationType
      operationSubtype
    }
  }
`;

export const operationClientUsageSparklinesQuery = gql<
  OperationClientUsageSparklinesQuery,
  OperationClientUsageSparklinesQueryVariables
>`
  query OperationClientUsageSparklinesQuery(
    $graphId: ID!
    $timeFrom: Timestamp!
    $timeTo: Timestamp = "-0"
    $resolution: Resolution!
    $filter: ServiceQueryStatsFilter
  ) {
    service(id: $graphId) {
      id
      totalCount: statsWindow(from: $timeFrom, to: $timeTo) {
        queryStats(filter: $filter) {
          ...TotalCountForSparklineFragment
        }
      }
      sparklines: statsWindow(
        from: $timeFrom
        to: $timeTo
        resolution: $resolution
      ) {
        queryStats(filter: $filter) {
          timestamp
          ...TotalCountForSparklineFragment
        }
      }
    }
  }
  ${TotalCountForSparklineFragment}
`;

interface Props {
  graphId: string;
  graphVariant?: string;
  queryId: string;
  limit?: number;
  range?: Parameters<typeof date.getDateBounds>[0];
  filter:
    | GraphQLTypes.ServiceErrorStatsFilter
    | GraphQLTypes.ServiceTracePathErrorsRefsFilter
    | GraphQLTypes.ServiceTraceRefsFilter
    | GraphQLTypes.ServiceQueryStatsFilter;
}

const OperationClientUsageCard = ({
  name,
  requests,
  data,
  graphId,
  graphVariant,
}: {
  name: string;
  requests: number;
  data: number[];
  graphId: string;
  graphVariant: string;
}) => (
  <Card className="w-[400px]">
    <CardHeader>
      <CardTitle>
        <Link
          className="block truncate"
          to={clientsRouteConfig.location({
            range: undefined,
            graphId,
            graphVariant,
            clientName: name,
          })}
        >
          {name}
        </Link>
      </CardTitle>
    </CardHeader>
    <CardBody className="w-full flex-row justify-between">
      <div className="flex flex-col">
        <Text variant="code" size="2xl">
          {format.number(requests, '0[.]0a')}
        </Text>
        <Heading as="div" size="lg" className="text-secondary">
          requests
        </Heading>
      </div>
      <div
        className="ml-auto flex items-center justify-center"
        css={css`
          svg {
            fill: none;
            stroke: ${customColors.code.b};
          }
        `}
      >
        <SparklineChart
          className="block"
          width={250}
          height={60}
          margin={4}
          data={data}
        />
      </div>
    </CardBody>
  </Card>
);

const SubscriptionClientUsageCard = ({
  name,
  registrations,
  notifications,
  graphId,
  graphVariant,
}: {
  name: string;
  registrations: number;
  notifications: number;
  graphId: string;
  graphVariant: string;
}) => (
  <Card className="w-[400px]">
    <CardHeader>
      <CardTitle>
        <Link
          className="block truncate"
          to={clientsRouteConfig.location({
            range: undefined,
            graphId,
            graphVariant,
            clientName: name,
          })}
        >
          {name}
        </Link>
      </CardTitle>
    </CardHeader>
    <CardBody className="flex-row gap-8">
      {/* Registrations */}
      <div className="flex flex-col">
        <Text variant="code" size="2xl">
          {format.number(registrations, '0[.]0a')}
        </Text>
        <Heading as="div" size="lg" className="text-secondary">
          registrations
        </Heading>
      </div>

      {/* Notifications */}
      <div className="flex flex-col">
        <Text variant="code" size="2xl">
          {format.number(notifications, '0[.]0a')}
        </Text>
        <Heading as="div" size="lg" className="text-secondary">
          notifications
        </Heading>
      </div>
    </CardBody>
  </Card>
);

export const OperationClientUsage = ({
  graphId,
  range,
  graphVariant,
  queryId,
  filter,
  limit = LIMIT,
}: Props) => {
  const { to, from, fastModeRange } = useRouteParams(insightsRouteConfig);
  const inBackground = useIsAppInBackgroundContext();
  const { timeFrom, timeTo, pollInterval, resolution } = useMemo(
    () =>
      date.getDateBounds(
        range || 'custom',
        { from, to },
        undefined,
        fastModeRange,
      ),
    [range, from, to, fastModeRange],
  );

  const variables = {
    limit,
    timeFrom,
    timeTo,
    graphId,
    graphVariant,
    resolution,
    filter: {
      queryId,
      ...filter,
    },
  };

  const { loading, error, data } = useQuery(
    operationClientUsageSparklinesQuery,
    {
      variables,
      skip: inBackground,
      skipPollAttempt: useCallback(() => inBackground, [inBackground]),
      pollInterval,
    },
  );

  const isSubscriptionOperation = data?.service?.sparklines?.queryStats.some(
    (row) => row.groupBy.operationType === 'subscription',
  );

  const clientsToSparklinesData: {
    [key: string]: {
      metrics: number[];
      totalCount?: number;
      totalRegistrations?: number;
      totalNotifications?: number;
    };
  } =
    data?.service?.sparklines?.queryStats.reduce<{
      [key: string]: {
        metrics: number[];
        totalCount?: number;
        totalRegistrations?: number;
        totalNotifications?: number;
      };
    }>((acc, curr) => {
      if (isSubscriptionOperation) {
        const clientName = curr.groupBy.clientName ?? UNIDENTIFIED_CLIENT;
        const totalRequests =
          curr.metrics.totalLatencyHistogram.totalCount ?? 0;
        const operationSubtype = curr.groupBy.operationSubtype ?? '';

        acc[clientName] = {
          metrics: [...(acc[clientName]?.metrics ?? []), totalRequests],
          totalNotifications:
            operationSubtype === 'subscription-event'
              ? (acc?.[clientName]?.totalNotifications ?? 0) + totalRequests
              : (acc?.[clientName]?.totalNotifications ?? 0),
          totalRegistrations:
            operationSubtype === 'subscription-request'
              ? (acc?.[clientName]?.totalRegistrations ?? 0) + totalRequests
              : (acc?.[clientName]?.totalRegistrations ?? 0),
        };

        return acc;
      } else {
        const clientName = curr.groupBy.clientName ?? UNIDENTIFIED_CLIENT;
        const totalRequests =
          curr.metrics.totalLatencyHistogram.totalCount ?? 0;

        acc[clientName] = {
          metrics: [...(acc[clientName]?.metrics ?? []), totalRequests],
          totalCount: (acc?.[clientName]?.totalCount ?? 0) + totalRequests,
        };

        return acc;
      }
    }, {}) ?? {};

  const totalCountForCard: {
    [key: string]: number;
  } =
    data?.service?.totalCount?.queryStats?.reduce<{
      [key: string]: number;
    }>((acc, curr) => {
      const clientName = curr.groupBy.clientName ?? UNIDENTIFIED_CLIENT;
      const totalRequests = curr.metrics.totalLatencyHistogram.totalCount ?? 0;

      acc[clientName] = (acc?.[clientName] ?? 0) + totalRequests;

      return acc;
    }, {}) ?? {};

  const entries = Object.entries(clientsToSparklinesData);
  if (isSubscriptionOperation) {
    entries.sort((clientA, clientB) => {
      // Sort on the total of notifications and registrations, if they exist.
      if (
        clientA[1]?.totalNotifications &&
        clientB[1]?.totalNotifications &&
        clientA[1]?.totalRegistrations &&
        clientB[1]?.totalRegistrations
      ) {
        return (
          clientB[1].totalNotifications +
          clientB[1].totalRegistrations -
          (clientA[1].totalNotifications + clientA[1].totalRegistrations)
        );
      } else {
        return 0;
      }
    });
  } else {
    entries.sort(
      (clientA, clientB) =>
        (clientB[1]?.totalCount ?? 0) - (clientA[1]?.totalCount ?? 0),
    );
  }

  const sortedClientsToSparklinesData = Object.fromEntries(
    entries.slice(0, limit),
  );
  const distinctClients = Object.keys(sortedClientsToSparklinesData).length;

  return (
    <Card variant="unstyled">
      <CardHeader>
        <CardTitle>
          Client usage <DateRangeBadge />
        </CardTitle>
        <CardDescription>
          {distinctClients > 1
            ? `These are the top ${Math.min(
                distinctClients,
                limit,
              )} clients that this operation reports to.`
            : 'This operation reports metrics from a single client.'}
        </CardDescription>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="flex h-36 items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <ErrorMessage />
        ) : distinctClients ? (
          <div className="hide-scrollbar flex w-full flex-row flex-wrap gap-4 overflow-x-scroll">
            {Object.entries(sortedClientsToSparklinesData)?.map((client) => {
              const name = client[0];
              const metrics = client[1].metrics;
              const totalCount = totalCountForCard[name];
              const totalRegistrations = client[1]?.totalRegistrations;
              const totalNotifications = client[1]?.totalNotifications;

              return isSubscriptionOperation ? (
                <SubscriptionClientUsageCard
                  name={name ?? ''}
                  registrations={totalRegistrations ?? 0}
                  notifications={totalNotifications ?? 0}
                  key={`subscription-${name}}`}
                  graphId={graphId}
                  graphVariant={graphVariant ?? ''}
                />
              ) : (
                <OperationClientUsageCard
                  name={name ?? ''}
                  graphId={graphId}
                  graphVariant={graphVariant ?? ''}
                  requests={totalCount ?? 0}
                  data={metrics}
                  key={`operation-usage-card-${name}`}
                />
              );
            })}
          </div>
        ) : (
          <div
            data-testid="no-clients-help"
            className="rounded-md bg-silver-light p-2 text-neutral"
          >
            <em>No client usage reported for this operation.</em>
            <p>
              Try adjusting your time range or enable client awareness so you
              can view your usage insights on a per-client basis.{' '}
              <ClickableText
                as="a"
                className="text-link underline"
                href={DocsRouter.path('ClientAwareness')}
                target="_blank"
                rel="noreferrer noopener"
              >
                Learn more <IconOutlink />
              </ClickableText>
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
};
